import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { LOCAL_STORAGE_KEYS } from '@savgroup-front-common/types';

import createGenericContext from '../../helpers/createGenericContext';

export interface NewUiContextArgs {
  isDarkModeEnabled: boolean;
  onToggleDarkMode: () => void;
}

const [useNewUiContext, NewUiContextProvider] =
  createGenericContext<NewUiContextArgs>();

const NewUiProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isDarkModeEnabled, setIsDarkModeEnabled] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.DARK_MODE,
    false,
    {
      raw: false,
      serializer: (value: boolean) => {
        if (value) {
          return 'true';
        }

        return 'false';
      },
      deserializer: (value: string) => {
        return value === 'true';
      },
    },
  );

  const [isDarkMode, setIsDarkMode] = useState(isDarkModeEnabled ?? false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const onToggleDarkMode = useCallback(() => {
    setIsDarkMode(!isDarkMode);
    setIsDarkModeEnabled(!isDarkMode);
  }, [isDarkMode, setIsDarkModeEnabled]);

  return (
    <NewUiContextProvider
      value={{
        isDarkModeEnabled: isDarkMode,
        onToggleDarkMode,
      }}
    >
      {children}
    </NewUiContextProvider>
  );
};

export { NewUiProvider, useNewUiContext };
